import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash, PencilSimple, Chalkboard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Modal, Field, Loader, Empty, fadeUp } from "../components/kit";

function ClassForm({ initial, staff, onClose, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState({ name: initial?.name || "", section: initial?.section || "A", class_teacher_id: initial?.class_teacher_id || "",
    subjects: (initial?.subjects || ["English", "Hindi", "Mathematics", "Science", "Social Science"]).join(", ") });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, class_teacher_id: f.class_teacher_id || null, subjects: f.subjects.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (initial?.id) await api.put(`/classes/${initial.id}`, body);
      else await api.post("/classes", body);
      toast.success("Class saved");
      onSaved();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={initial?.id ? t("Edit") : t("Add class")} testid="class-form-dialog">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("Name")}><input className="field" required placeholder="Class 9" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="class-name-input" /></Field>
          <Field label="Section"><input className="field" required value={f.section} onChange={(e) => setF({ ...f, section: e.target.value })} data-testid="class-section-input" /></Field>
        </div>
        <Field label="Class teacher">
          <select className="field" value={f.class_teacher_id} onChange={(e) => setF({ ...f, class_teacher_id: e.target.value })} data-testid="class-teacher-select">
            <option value="">—</option>
            {staff?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Subjects (comma separated)"><input className="field" value={f.subjects} onChange={(e) => setF({ ...f, subjects: e.target.value })} data-testid="class-subjects-input" /></Field>
        <button className="btn-primary w-full" data-testid="class-submit-btn">{t("Save")}</button>
      </form>
    </Modal>
  );
}

export default function Classes() {
  const { t } = useT();
  const { data, loading, reload } = useApi("/classes");
  const { data: staff } = useApi("/staff");
  const [form, setForm] = useState(null);
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name} ${c.section}?`)) return;
    try {
      await api.delete(`/classes/${c.id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Structure" title={t("Classes")} subtitle="Sections, class teachers and subjects."
        actions={<button className="btn-primary" onClick={() => setForm({})} data-testid="add-class-btn"><Plus size={16} weight="bold" /> {t("Add class")}</button>} />
      {loading ? <Loader /> : !data?.length ? <GlassCard><Empty /></GlassCard> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {data.map((c, i) => (
            <motion.div key={c.id} {...fadeUp(i)}>
              <GlassCard hover className="p-6 h-full relative overflow-hidden" data-testid={`class-card-${c.id}`}>
                <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-blue-500/15 blur-2xl" />
                <div className="relative flex items-start justify-between">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/5 border border-white/10 text-blue-300"><Chalkboard size={24} weight="duotone" /></span>
                  <div className="flex gap-1">
                    <button className="btn-ghost !p-2" onClick={() => setForm(c)} data-testid={`class-edit-${c.id}`}><PencilSimple size={15} /></button>
                    <button className="btn-ghost !p-2 !text-rose-300" onClick={() => del(c)} data-testid={`class-delete-${c.id}`}><Trash size={15} /></button>
                  </div>
                </div>
                <h3 className="relative mt-5 font-display text-2xl font-semibold text-white">{c.name} <span className="text-orange-300">{c.section}</span></h3>
                <p className="text-sm text-zinc-400">{c.student_count} {t("Students").toLowerCase()} · {c.class_teacher_name || "No class teacher"}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {c.subjects.map((s) => <span key={s} className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-zinc-300">{s}</span>)}
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      )}
      {form && <ClassForm initial={form.id ? form : null} staff={staff} onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />}
    </div>
  );
}
